import { createContext, useContext, useEffect, useState, useCallback, useMemo, ReactNode } from 'react'
import { useAppSelector } from '@crm-src/lib/store'
import webSocketService, { type WebSocketMessage, type CampaignUpdate, type ImportProgress } from '@crm-src/lib/websocket'

interface WebSocketContextType {
  isConnected: boolean
  connectionError: string | null
  onCampaignUpdate: (callback: (update: CampaignUpdate) => void) => () => void
  onImportProgress: (callback: (update: ImportProgress) => void) => () => void
  onMessage: (callback: (message: WebSocketMessage) => void) => () => void
  requestCampaignsUpdate: () => void
}

const WebSocketContext = createContext<WebSocketContextType | null>(null)

export function WebSocketProvider({ children }: { children: ReactNode }) {
  const isAuthenticated = useAppSelector(state => state.auth.isAuthenticated)
  const [isConnected, setIsConnected] = useState(false)
  const [connectionError, setConnectionError] = useState<string | null>(null)

  useEffect(() => {
    const initializeConnection = async () => {
      if (isAuthenticated && !isConnected) {
        // Get auth token from localStorage (same key as API client uses)
        const token = localStorage.getItem('xop_crm_token')

        if (token) {
          
          try {
            await webSocketService.connect(token)
            setIsConnected(true)
            setConnectionError(null)
          } catch (error: any) {
            console.error('WebSocketProvider: ❌ Global WebSocket connection failed', error)
            setIsConnected(false)
            setConnectionError(error.message)
          }
        } else {
          console.warn('WebSocketProvider: ⚠️ No auth token found in localStorage (xop_crm_token)')
          setConnectionError('No authentication token available')
        }
      } else if (!isAuthenticated && isConnected) {
        // Disconnect when user logs out
        webSocketService.disconnect()
        setIsConnected(false)
        setConnectionError(null)
      }
    }

    initializeConnection()

    // Cleanup on unmount
    return () => {
      if (isConnected) {
        webSocketService.disconnect()
        setIsConnected(false)
      }
    }
  }, [isAuthenticated]) // Remove isConnected from dependencies to avoid loops

  // Stable callback references to prevent unnecessary re-subscriptions in consumers
  const onCampaignUpdate = useCallback((callback: (update: CampaignUpdate) => void) => {
    return webSocketService.on('campaign_status_update', callback)
  }, [])

  const onImportProgress = useCallback((callback: (update: ImportProgress) => void) => {
    return webSocketService.on('import_progress', callback)
  }, [])

  const onMessage = useCallback((callback: (message: WebSocketMessage) => void) => {
    return webSocketService.on('all', callback)
  }, [])

  const requestCampaignsUpdate = useCallback(() => {
    webSocketService.requestCampaignsUpdate()
  }, [])

  // Context value with stable references
  const contextValue: WebSocketContextType = useMemo(() => ({
    isConnected,
    connectionError,
    onCampaignUpdate,
    onImportProgress,
    onMessage,
    requestCampaignsUpdate,
  }), [isConnected, connectionError, onCampaignUpdate, onImportProgress, onMessage, requestCampaignsUpdate])

  return (
    <WebSocketContext.Provider value={contextValue}>
      {children}
    </WebSocketContext.Provider>
  )
}

export function useWebSocketContext(): WebSocketContextType {
  const context = useContext(WebSocketContext)
  if (!context) {
    throw new Error('useWebSocketContext must be used within a WebSocketProvider')
  }
  return context
}

export default WebSocketProvider
